import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ChartTooltipProvider, StateBlock, ToastProvider } from '@w7/ui'
import { AppShell } from './AppShell'
import { AuthGate } from './AuthGate'
import { DateRangeProvider } from './range'
import { GlobalSearchProvider } from '@/features/search'

import { LoginPage } from '@/features/auth/LoginPage'
import { CallbackPage } from '@/features/auth/CallbackPage'
import { NoAccessPage } from '@/features/auth/NoAccessPage'
import { OverviewPage } from '@/features/overview/OverviewPage'
import { DownlinePage } from '@/features/downline/DownlinePage'
import { MemberPage } from '@/features/member/MemberPage'
import { CreateAccountPage } from '@/features/create/CreateAccountPage'
import { ExposurePage } from '@/features/exposure/ExposurePage'
import { MarketBookPage } from '@/features/exposure/MarketBookPage'
import { MyPnlPage } from '@/features/reports/MyPnlPage'
import { MemberPnlPage } from '@/features/reports/MemberPnlPage'
import { TransfersPage } from '@/features/reports/TransfersPage'
import { TurnoverPage } from '@/features/reports/TurnoverPage'
import { CommissionPage } from '@/features/reports/CommissionPage'
import { ActivityPage } from '@/features/activity/ActivityPage'
import { SecuritySettingsPage } from '@/features/settings/SecuritySettingsPage'
import { MfaEnrollPage } from '@/features/settings/MfaEnrollPage'

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={client}>
      <DateRangeProvider>
        <ToastProvider>
          <ChartTooltipProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/callback" element={<CallbackPage />} />
                <Route path="/no-access" element={<NoAccessPage />} />
                <Route
                  path="*"
                  element={
                    <AuthGate>
                      {/* ⌘K. Three constraints fix this exact position:
                          · inside `<BrowserRouter>` — the palette navigates;
                          · inside `<AuthGate>` — every result is a downline
                            account, a market or a ledger ref, so the palette has
                            nothing it may show before there is a session, and
                            searching from /login would put an unauthenticated
                            read on the wire the moment the shortcut was pressed;
                          · OUTSIDE the inner `<Routes>` — it must survive the
                            navigation it causes. That works because this whole
                            `path="*"` element stays mounted across every in-app
                            route; only the inner `<Routes>` swaps. Mounted one
                            level deeper it would unmount itself mid-transition. */}
                      <GlobalSearchProvider>
                        <AppShell>
                          <Routes>
                            <Route path="/" element={<OverviewPage />} />
                            <Route path="/downline" element={<DownlinePage />} />
                            <Route path="/member/:id" element={<MemberPage />} />
                            <Route path="/create" element={<CreateAccountPage />} />
                            <Route path="/exposure" element={<ExposurePage />} />
                            <Route path="/exposure/market/:marketId" element={<MarketBookPage />} />
                            <Route path="/reports/my-pnl" element={<MyPnlPage />} />
                            <Route path="/reports/member-pnl" element={<MemberPnlPage />} />
                            <Route path="/reports/transfers" element={<TransfersPage />} />
                            <Route path="/reports/turnover" element={<TurnoverPage />} />
                            <Route path="/reports/commission" element={<CommissionPage />} />
                            <Route path="/activity" element={<ActivityPage />} />
                            <Route path="/settings/security" element={<SecuritySettingsPage />} />
                            <Route path="/settings/security/:methodId" element={<MfaEnrollPage />} />
                            <Route path="/reports" element={<Navigate to="/reports/my-pnl" replace />} />
                            <Route
                              path="*"
                              element={
                                <StateBlock
                                  icon="search"
                                  title="That page does not exist"
                                >
                                  The link may be stale, or it points at something outside your downline.
                                </StateBlock>
                              }
                            />
                          </Routes>
                        </AppShell>
                      </GlobalSearchProvider>
                    </AuthGate>
                  }
                />
              </Routes>
            </BrowserRouter>
          </ChartTooltipProvider>
        </ToastProvider>
      </DateRangeProvider>
    </QueryClientProvider>
  )
}
